<template>
  <v-navigation-drawer
    v-model="drawerVisible"
    fixed
    right
    temporary
    hide-overlay
    width="680"
    style="z-index: 9999;"
    class="d-flex flex-column elevation-12 border-left"
  >
    <!-- Header del Drawer / Modal -->
    <v-toolbar flat color="white" class="border-bottom flex-grow-0" height="60">
      <v-icon left :color="isEdit ? 'amber darken-3' : (isSimilar ? 'teal darken-2' : 'primary')">
        {{ isEdit ? 'mdi-pencil-box-outline' : (isSimilar ? 'mdi-content-duplicate' : 'mdi-plus-box-outline') }}
      </v-icon>
      <v-toolbar-title class="text-subtitle-1 font-weight-bold grey--text text--darken-4">
        {{ isEdit ? 'Editar Línea Comercial' : (isSimilar ? 'Agregar Línea Comercial Similar' : 'Nueva Línea Comercial') }}
      </v-toolbar-title>
      <v-spacer />
      <v-btn icon @click="closeDrawer"><v-icon>mdi-close</v-icon></v-btn>
    </v-toolbar>

    <!-- Formulario con Scroll -->
    <div class="flex-grow-1 overflow-y-auto pa-4" style="background-color: #f8fafc;">
      <!-- Alerta si es volumen Sin Cliente -->
      <v-alert
        v-if="isFromNoClient"
        dense
        outlined
        type="info"
        color="indigo darken-2"
        class="mb-3 rounded-lg text-caption bg-white"
      >
        <strong>Asignación de Volumen Sin Cliente:</strong> Este volumen proviene de la reserva general presupuestada. Seleccione el cliente comercial destino para asignar esta línea.
      </v-alert>

      <!-- 1. Producto y Condiciones Comerciales -->
      <v-card class="rounded-xl border-light bg-white pa-3 mb-3" elevation="0">
        <div class="caption font-weight-bold grey--text text--darken-3 mb-2 d-flex align-center">
          <v-icon x-small color="primary" class="mr-1">mdi-package-variant-closed</v-icon>
          1. PRODUCTO Y CONDICIONES COMERCIALES
        </div>

        <v-row dense>
          <!-- Cliente Comercial: Si es Sin Cliente se permite seleccionar el cliente destino -->
          <v-col cols="12" v-if="isFromNoClient">
            <sselect-client-sap
              label="Cliente Comercial Destino *"
              return-object
              v-model="destinationClientObj"
              :text="lineData.EpdCustomerName"
              @input="onDestinationClientSelected"
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" v-else>
            <s-text
              label="Cliente Comercial *"
              :value="lineData.EpdCustomerName || 'Sin Cliente'"
              readonly
              disabled
              dense
              hide-details
            />
          </v-col>

          <!-- Producto / Artículo SAP (s-article-search) -->
          <v-col cols="12" sm="8">
            <s-article-search
              label="Artículo SAP (Producto) *"
              v-model="selectedArticle"
              :readonly="isView"
              clearable
              placeholder="Buscar artículo en SAP por código o nombre..."
              @selected="onArticleSelected"
              @input="onArticleInput"
            />
          </v-col>

          <!-- Código de Artículo SAP -->
          <v-col cols="12" sm="4">
            <s-text
              label="Código de Artículo SAP *"
              :value="lineData.EpdSapCode || 'Sin código'"
              readonly
              disabled
              dense
              hide-details
            />
          </v-col>

          <!-- Empaque / Presentación (Catálogo GenDefinition 1341) -->
          <v-col cols="12" sm="6">
            <s-select-definition
              v-model="lineData.EpdPackaging"
              :def="1341"
              :id="'DedDescription'"
              label="Empaque / Presentación *"
              clearable
              dense
              hide-details
              @input="onPackagingSelected"
            />
          </v-col>

          <!-- Marca (Catálogo GenDefinition 1749) -->
          <v-col cols="6" sm="6">
            <s-select-definition
              v-model="lineData.EpdBrand"
              :def="1749"
              :id="'DedDescription'"
              label="Marca"
              add
              clearable
              dense
              hide-details
              @input="onBrandSelected"
            />
          </v-col>

          <!-- Ejecutivo Comercial (Usuario Logeado por defecto) -->
          <v-col cols="12" sm="6">
            <s-text
              label="Ejecutivo Comercial"
              :value="lineData.EpdExecutive || currentUserName"
              readonly
              disabled
              dense
              hide-details
            />
          </v-col>

          <!-- Tipo Bulk / Retail (Catálogo GenDefinition 1747) -->
          <v-col cols="6" sm="6">
            <s-select-definition
              v-model="lineData.EpdCommercialType"
              :def="1747"
              label="Tipo Comercial *"
              dense
              hide-details
            />
          </v-col>

          <!-- Incoterm (Catálogo GenDefinition 1742) -->
          <v-col cols="6" sm="6">
            <s-select-definition
              v-model="lineData.TypeIncoterm"
              :def="1742"
              label="Incoterm *"
              dense
              hide-details
              @input="onIncotermChanged"
            />
          </v-col>

          <!-- Puerto / Destino (Búsqueda SAP U_SYP_PTODESTINO) -->
          <v-col cols="12" sm="6">
            <s-search-modal
              label="Puerto Destino"
              v-model="lineData.EpdPortDestination"
              list-code="U_SYP_PTODESTINO"
              :headers="headersPtoDestino"
              :extra-param="lineData.PrsCustomerID || (selectedCustomer ? selectedCustomer.customerId : null)"
            ></s-search-modal>
          </v-col>

          <!-- Condición de Pago -->
          <v-col cols="12">
            <s-select-paymentterms
              :value="paymentConditionValue"
              label="Condición de Pago"
              :disabled="isView"
              :text="lineData.EpdPaymentCondition"
              :return-object="true"
              clearable
              @input="onPaymentConditionSelected"
            />
          </v-col>
        </v-row>
      </v-card>

      <!-- 2. Volumen y Capacidad Configurable -->
      <v-card class="rounded-xl border-light bg-white pa-3 mb-3" elevation="0">
        <div class="caption font-weight-bold grey--text text--darken-3 mb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon x-small color="primary" class="mr-1">mdi-weight-kilogram</v-icon>
            2. VOLUMEN Y CAPACIDAD
          </div>
          <div class="d-flex align-center">
            <v-chip
              x-small
              :color="capacityStatusColor"
              class="font-weight-bold mr-2"
              :text-color="capacityStatusTextColor"
            >
              {{ capacityStatusText }}
            </v-chip>
            <v-btn
              text
              x-small
              color="primary"
              class="text-capitalize font-weight-bold px-1"
              @click="showCapacityMaintenance = true"
              title="Abrir Maestro de Capacidades"
            >
              <v-icon x-small left>mdi-cog</v-icon>
              Maestro
            </v-btn>
          </div>
        </div>

        <v-row dense>
          <!-- Base de Ingreso -->
          <v-col cols="12" sm="6">
            <s-select
              v-model.number="lineData.EpdEntryBase"
              :items="entryBaseOptions"
              label="Base de Ingreso *"
              dense
              hide-details
              @input="recalculate"
            />
          </v-col>

          <!-- Capacidad TM/FCL (Configurable / Editable si es Excepción) -->
          <v-col cols="12" sm="6">
            <s-text
              label="Capacidad TM por FCL *"
              decimal
              v-model.number="lineData.EpdCapacity"
              placeholder="ej: 21.60"
              :disabled="!isExceptionMode && !!matchedCapacityConfig"
              :readonly="!isExceptionMode && !!matchedCapacityConfig"
              dense
              hide-details
              :error="!lineData.EpdCapacity || lineData.EpdCapacity <= 0"
              @input="recalculate"
            />
            <div v-if="!lineData.EpdCapacity || lineData.EpdCapacity <= 0" class="caption red--text text--darken-3 font-weight-medium mt-1">
              ⚠️ Capacidad no configurada. Configure en el maestro o active una excepción.
            </div>
            <div v-else-if="matchedCapacityConfig && !isExceptionMode" class="caption green--text text--darken-3 font-weight-medium mt-1">
              ✓ Configuración #{{ matchedCapacityConfig.CpcID }}: {{ matchedCapacityConfig.CpcCapacityTonsFcl }} TM/FCL
            </div>
            <div v-else-if="isExceptionMode" class="caption amber--text text--darken-4 font-weight-bold mt-1">
              ⚡ Excepción activa: Capacidad manual ({{ lineData.EpdCapacity }} TM/FCL)
            </div>
          </v-col>

          <!-- Switch de Excepción / Capacidad diferente -->
          <v-col cols="12" class="py-0">
            <div class="d-flex align-center justify-space-between mt-1 mb-1">
              <v-switch
                v-model="isExceptionMode"
                dense
                hide-details
                class="mt-0 pt-0"
                color="amber darken-3"
                @change="onExceptionToggle"
              >
                <template v-slot:label>
                  <span class="caption font-weight-bold grey--text text--darken-3">
                    Usar capacidad diferente / Excepción
                  </span>
                </template>
              </v-switch>
            </div>
          </v-col>

          <!-- Motivo de Excepción (Obligatorio si isExceptionMode es true) -->
          <v-col cols="12" v-if="isExceptionMode" class="mb-2">
            <s-text
              label="Motivo de Excepción de Capacidad *"
              v-model="lineData.EpdCapacityExceptionReason"
              placeholder="Explique el motivo del cambio de capacidad para trazabilidad..."
              dense
              hide-details
              :error="!lineData.EpdCapacityExceptionReason || !lineData.EpdCapacityExceptionReason.trim()"
            />
            <div v-if="!lineData.EpdCapacityExceptionReason || !lineData.EpdCapacityExceptionReason.trim()" class="caption red--text mt-1">
              * Debe ingresar el motivo de la excepción para registrar esta línea.
            </div>
          </v-col>

          <!-- TM Programadas -->
          <v-col cols="6">
            <s-text
              label="TM Programadas *"
              decimal
              v-model.number="lineData.EpdProgrammedTons"
              :disabled="lineData.EpdEntryBase === 2"
              dense
              hide-details
              @input="recalculate"
            />
            <div v-if="lineData.EpdEntryBase === 2" class="caption grey--text mt-1">
              Calculado: {{ formatNumber(lineData.EpdProgrammedFcl, 2) }} FCL × {{ lineData.EpdCapacity || 0 }} TM
            </div>
            <div v-else class="caption primary--text font-weight-medium mt-1">
              Campo de ingreso directo
            </div>
          </v-col>

          <!-- FCL Programados -->
          <v-col cols="6">
            <s-text
              label="FCL Programados *"
              decimal
              v-model.number="lineData.EpdProgrammedFcl"
              :disabled="lineData.EpdEntryBase === 1"
              dense
              hide-details
              @input="recalculate"
            />
            <div v-if="lineData.EpdEntryBase === 1" class="caption grey--text mt-1">
              Calculado: {{ formatNumber(lineData.EpdProgrammedTons, 2) }} TM / {{ lineData.EpdCapacity || 1 }} TM
            </div>
            <div v-else class="caption primary--text font-weight-medium mt-1">
              Campo de ingreso directo
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- 3. Precios e Importes según Incoterm -->
      <v-card class="rounded-xl border-light bg-white pa-3 mb-3" elevation="0">
        <div class="caption font-weight-bold grey--text text--darken-3 mb-2 d-flex align-center">
          <v-icon x-small color="primary" class="mr-1">mdi-currency-usd</v-icon>
          3. PRECIOS E IMPORTES
        </div>

        <v-row dense>
          <!-- Precio Pactado USD/kg -->
          <v-col cols="12" sm="6">
            <s-text
              label="Precio Pactado (USD/kg) *"
              decimal
              v-model.number="lineData.EpdPriceAgreed"
              dense
              hide-details
              @input="onPriceAgreedChanged"
            />
          </v-col>

          <!-- Precio FOB Equivalente USD/kg -->
          <v-col cols="12" sm="6">
            <s-text
              label="Precio FOB Eq (USD/kg) *"
              decimal
              v-model.number="lineData.EpdPriceFobEquivalent"
              :disabled="isFobIncoterm"
              dense
              hide-details
              @input="recalculate"
            />
            <div v-if="isFobIncoterm" class="caption grey--text mt-1">
              Incoterm FOB: igual al precio pactado
            </div>
            <div v-else class="caption grey--text text--darken-2 mt-1">
              Ingrese el valor base FOB para calcular diferencial
            </div>
          </v-col>

          <!-- Diferencial USD/kg (Fórmula: CFR - FOB) -->
          <v-col cols="6">
            <s-text
              label="Diferencial (USD/kg)"
              :value="`$${formatNumber(lineData.EpdPriceDifferential, 4)}`"
              readonly
              disabled
              dense
              hide-details
            />
            <div class="caption grey--text mt-1">
              {{ isFobIncoterm ? 'FOB: 0.00' : 'Diferencial (Pactado - FOB Eq)' }}
            </div>
          </v-col>

          <!-- Precio de Mercado (Informativo y Opcional) -->
          <v-col cols="6">
            <s-text
              label="Precio Mercado (USD/kg - Opcional)"
              decimal
              v-model.number="lineData.EpdMarketPrice"
              placeholder="ej: 3.50"
              dense
              hide-details
            />
            <div class="caption grey--text mt-1">
              Dato referencial e informativo
            </div>
          </v-col>

          <!-- Importe Contractual -->
          <v-col cols="6">
            <s-text
              label="Importe Contractual (USD)"
              :value="`$${formatNumber(lineData.EpdAmountContract, 2)}`"
              readonly
              disabled
              dense
              hide-details
            />
            <div class="caption grey--text mt-1">
              TM × 1000 × Precio Pactado
            </div>
          </v-col>

          <!-- FOB Eq Total -->
          <v-col cols="6">
            <s-text
              label="Importe FOB Eq Total (USD)"
              :value="`$${formatNumber(lineData.EpdAmountFobEquivalent, 2)}`"
              readonly
              disabled
              dense
              hide-details
            />
            <div class="caption green--text text--darken-3 font-weight-medium mt-1">
              TM × 1000 × Precio FOB Eq
            </div>
          </v-col>

          <!-- Observación de Línea -->
          <v-col cols="12" class="mt-2">
            <s-text
              label="Observación de la Línea"
              v-model="lineData.EpdObservation"
              placeholder="Detalles específicos de esta línea..."
              dense
              hide-details
              :maxlength="300"
            />
          </v-col>
        </v-row>
      </v-card>
    </div>

    <!-- Footer del Drawer con Impacto en Saldo y Acciones -->
    <div class="pa-3 bg-white border-top flex-grow-0">
      <!-- Mini Preview de Impacto en Saldo del Cliente -->
      <div class="pa-2 mb-2 rounded-lg bg-grey-lighten-5 border-light d-flex justify-space-between align-center text-caption">
        <div>
          Línea: <strong>{{ formatNumber(lineData.EpdProgrammedTons, 2) }} TM</strong> ({{ formatNumber(lineData.EpdProgrammedFcl, 2) }} FCL) | FOB: <strong>${{ formatNumber(lineData.EpdAmountFobEquivalent, 2) }}</strong>
        </div>
        <div :class="projectedBalance < 0 ? 'red--text font-weight-bold' : 'green--text text--darken-3 font-weight-bold'">
          Saldo restante: {{ formatNumber(projectedBalance, 2) }} TM
          <span v-if="projectedBalance < 0">⚠️</span>
        </div>
      </div>

      <div class="d-flex align-center justify-space-between">
        <v-btn text small class="text-capitalize" @click="closeDrawer">
          Cancelar
        </v-btn>

        <div class="d-flex align-center gap-2">
          <v-btn
            v-if="!isEdit"
            outlined
            color="primary"
            small
            class="text-capitalize rounded-lg font-weight-bold"
            @click="submitLine(true)"
          >
            Guardar y Seguir Agregando
          </v-btn>

          <v-btn
            color="primary"
            small
            class="text-capitalize rounded-lg px-4 font-weight-bold"
            @click="submitLine(false)"
          >
            <v-icon left small>mdi-check</v-icon>
            {{ isEdit ? 'Actualizar Línea' : 'Guardar Línea' }}
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Modal Mantenimiento de Capacidades -->
    <commercial-capacity-config-modal
      v-model="showCapacityMaintenance"
      :default-division="division"
      @config-updated="loadActiveCapacities"
    />
  </v-navigation-drawer>
</template>

<script>
import sArticleSearch from "@/components/Utils/Logistics/SArticleSearch";
import SSelectPaymentterms from "@/components/logistics/SSelectPaymentterms.vue";
import CommercialCapacityConfigModal from "./CommercialCapacityConfigModal.vue";
import CommercialCapacityConfigService from "@/services/ExternalTrade/CommercialCapacityConfigService";
import _sUserService from "@/services/Security/UserService";
import SselectClientSap from "@/components/Sale/SselectClientSap.vue";

import {
  recalculateLine,
  validateLine,
  formatNumber,
} from "../utils/CommercialProgrammingCalculations";

export default {
  name: "CommercialLineModal",
  components: {
    sArticleSearch,
    SSelectPaymentterms,
    CommercialCapacityConfigModal,
    SselectClientSap
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    isSimilar: {
      type: Boolean,
      default: false,
    },
    isView: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({}),
    },
    selectedCustomer: {
      type: Object,
      default: null,
    },
    otherLinesForClient: {
      type: Array,
      default: () => [],
    },
    division: {
      type: Number,
      default: 1, // 1: Frescos, 2: IQF
    },
  },
  data() {
    return {
      lineData: {},
      selectedArticle: null,
      destinationClientObj: null,
      matchedCapacityConfig: null,
      activeCapacityConfigs: [],
      showCapacityMaintenance: false,
      isExceptionMode: false,
      entryBaseOptions: [
        { text: "Toneladas (TM)", value: 1 },
        { text: "Contenedores (FCL)", value: 2 },
      ],
      headersPtoDestino: [
        { text: "Destino", value: "value" },
      ],
    };
  },
  computed: {
    drawerVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    paymentConditionValue() {
      return this.lineData.PytID || this.lineData.EpdPaymentCondition || null;
    },
    isFobIncoterm() {
      return this.lineData.TypeIncoterm === 1 || !this.lineData.TypeIncoterm;
    },
    isFromNoClient() {
      return (
        this.lineData.PrsCustomerOriginID === "Sin Cliente" ||
        (this.selectedCustomer && (this.selectedCustomer.customerId === null || this.selectedCustomer.customerName === "Sin Cliente"))
      );
    },
    capacityStatusText() {
      if (this.isExceptionMode) {
        return `Excepción: ${this.lineData.EpdCapacity || 0} TM/FCL`;
      }
      if (!this.lineData.EpdCapacity || this.lineData.EpdCapacity <= 0) {
        return "Capacidad no configurada";
      }
      return `${this.lineData.EpdCapacity} TM/FCL`;
    },
    capacityStatusColor() {
      if (this.isExceptionMode) {
        return "amber lighten-4";
      }
      if (!this.lineData.EpdCapacity || this.lineData.EpdCapacity <= 0) {
        return "red lighten-5";
      }
      return "green lighten-5";
    },
    capacityStatusTextColor() {
      if (this.isExceptionMode) {
        return "amber darken-4";
      }
      if (!this.lineData.EpdCapacity || this.lineData.EpdCapacity <= 0) {
        return "red darken-4";
      }
      return "green darken-4";
    },
    projectedBalance() {
      if (!this.selectedCustomer) return 0;
      const baseBudget = this.selectedCustomer.budgetTons || 0;
      const otherTons = this.otherLinesForClient.reduce(
        (sum, l) => sum + (parseFloat(l.EpdProgrammedTons) || 0),
        0
      );
      const thisLineTons = parseFloat(this.lineData.EpdProgrammedTons) || 0;
      return baseBudget - (otherTons + thisLineTons);
    },
    currentUserId() {
      const id = localStorage.getItem("UsrID");
      return id ? parseInt(id) : (this.$fun ? this.$fun.getUserID() : null);
    },
    currentUserName() {
      const userInfoStr = localStorage.getItem("UserInfo");
      if (userInfoStr) {
        try {
          const u = JSON.parse(userInfoStr);
          if (u) {
            const parts = [u.NtpName, u.NtpPaternalSurname, u.NtpMaternalSurname].filter(Boolean);
            if (parts.length > 0) return parts.join(" ");
          }
        } catch (e) {}
      }
      return localStorage.getItem("UsrFullName") || localStorage.getItem("UsrName") || "";
    },
  },
  created() {
    this.loadLoggedUserFullName();
    this.loadActiveCapacities();
  },
  watch: {
    value(val) {
      if (val) {
        this.lineData = JSON.parse(JSON.stringify(this.initialData));

        // Asignar ejecutivo comercial del usuario logeado por defecto con nombre completo
        if (!this.lineData.UsrExecutiveID) {
          this.lineData.UsrExecutiveID = this.currentUserId;
        }
        if (!this.lineData.EpdExecutive || this.lineData.EpdExecutive === localStorage.getItem("UsrName")) {
          this.lineData.EpdExecutive = this.currentUserName;
        }

        this.loadLoggedUserFullName();

        // Inicializar selector de artículo SAP
        if (this.lineData.EpdSapCode || this.lineData.EpdProduct) {
          this.selectedArticle = {
            ArtCode: this.lineData.EpdSapCode || "",
            ArtName: this.lineData.EpdProduct || "",
          };
        } else {
          this.selectedArticle = null;
        }

        // Asegurar que campos de texto sean cadenas limpias
        this.lineData.EpdPackaging = this.lineData.EpdPackaging || "";
        this.lineData.EpdBrand = this.lineData.EpdBrand || "";
        this.lineData.EpdPaymentCondition = this.lineData.EpdPaymentCondition || "";

        // Inicializar cliente destino si viene de reserva
        this.destinationClientObj = this.lineData.PrsCustomerID
          ? { CardCode: this.lineData.PrsCustomerID, CardName: this.lineData.EpdCustomerName }
          : null;

        // Verificar si la línea inicial ya era una excepción
        this.isExceptionMode = this.lineData.EpdIsCapacityException === 1;

        this.loadActiveCapacities().then(() => {
          this.lookupCapacityConfig(false);
          this.recalculate();
        });
      }
    },
  },
  methods: {
    formatNumber,
    async loadActiveCapacities() {
      try {
        const filter = {
          TypeDestination: this.division || 1,
          SecStatus: 1,
          OnlyActiveValidity: true,
        };
        const requestID = this.$fun.getUserID();
        const res = await CommercialCapacityConfigService.list(filter, requestID);
        if (res && res.data) {
          this.activeCapacityConfigs = res.data;
          this.lookupCapacityConfig(false);
        }
      } catch (e) {
        console.error("Error al cargar capacidades activas:", e);
      }
    },
    async loadLoggedUserFullName() {
      const userInfoStr = localStorage.getItem("UserInfo");
      if (userInfoStr) {
        try {
          const u = JSON.parse(userInfoStr);
          if (u) {
            const parts = [u.NtpName, u.NtpPaternalSurname, u.NtpMaternalSurname].filter(Boolean);
            if (parts.length > 0) {
              const fullName = parts.join(" ");
              localStorage.setItem("UsrFullName", fullName);
              if (!this.lineData.EpdExecutive || this.lineData.EpdExecutive === localStorage.getItem("UsrName")) {
                this.$set(this.lineData, "EpdExecutive", fullName);
              }
              return fullName;
            }
          }
        } catch (e) {}
      }
      const stored = localStorage.getItem("UsrFullName");
      if (stored) {
        if (!this.lineData.EpdExecutive || this.lineData.EpdExecutive === localStorage.getItem("UsrName")) {
          this.$set(this.lineData, "EpdExecutive", stored);
        }
        return stored;
      }
      try {
        const res = await _sUserService.listActive(this.$fun ? this.$fun.getUserID() : 0);
        if (res && res.data) {
          const currentId = this.currentUserId;
          const user = res.data.find((u) => u.UsrID === currentId);
          if (user && (user.UsrFullName || user.UsrName)) {
            const fullName = user.UsrFullName || user.UsrName;
            localStorage.setItem("UsrFullName", fullName);
            if (!this.lineData.EpdExecutive || this.lineData.EpdExecutive === localStorage.getItem("UsrName")) {
              this.$set(this.lineData, "EpdExecutive", fullName);
            }
            return fullName;
          }
        }
      } catch (e) {
        console.error("Error al cargar nombre completo de usuario:", e);
      }
      return localStorage.getItem("UsrName") || "";
    },
    onArticleSelected(art) {
      if (art) {
        this.lineData.EpdSapCode = art.ArtCode || "";
        this.lineData.EpdProduct = art.ArtName || "";
      } else {
        this.lineData.EpdSapCode = "";
        this.lineData.EpdProduct = "";
      }
      this.lookupCapacityConfig(true);
      this.recalculate();
    },
    onArticleInput(art) {
      if (!art) {
        this.lineData.EpdSapCode = "";
        this.lineData.EpdProduct = "";
      }
    },
    onPackagingSelected(pkg) {
      if (pkg) {
        this.lineData.EpdPackaging = typeof pkg === "object" ? (pkg.DedDescription || pkg.text || pkg.label || "") : String(pkg);
      } else {
        this.lineData.EpdPackaging = "";
      }
      this.lookupCapacityConfig(true);
      this.recalculate();
    },
    onBrandSelected(brand) {
      if (brand) {
        this.lineData.EpdBrand = typeof brand === "object" ? (brand.DedDescription || brand.text || brand.label || "") : String(brand);
      } else {
        this.lineData.EpdBrand = "";
      }
    },
    onPaymentConditionSelected(item) {
      if (item && typeof item === "object") {
        this.lineData.PytID = item.PytID || item.value || null;
        this.lineData.EpdPaymentCondition = item.PytDescription || item.text || item.Description || "";
      } else if (typeof item === "string") {
        this.lineData.EpdPaymentCondition = item;
      } else {
        this.lineData.PytID = null;
        this.lineData.EpdPaymentCondition = "";
      }
    },
    onExceptionToggle(val) {
      if (val) {
        this.lineData.EpdIsCapacityException = 1;
      } else {
        this.lineData.EpdIsCapacityException = 0;
        this.lineData.EpdCapacityExceptionReason = "";
        this.lookupCapacityConfig(true);
      }
      this.recalculate();
    },
    lookupCapacityConfig(overrideExisting = true) {
      const prod = (this.lineData.EpdProduct || "").trim().toUpperCase();
      const pkg = (this.lineData.EpdPackaging || "").trim().toUpperCase();
      const sapCode = (this.lineData.EpdSapCode || "").trim().toUpperCase();

      if (!prod) {
        this.matchedCapacityConfig = null;
        if (!this.isExceptionMode) {
          this.lineData.CpcID = null;
        }
        return;
      }

      const match = (this.activeCapacityConfigs || []).find((c) => {
        const divMatch = !c.TypeDestination || c.TypeDestination === this.division;
        const sapMatch = !c.CpcSapCode || !sapCode || c.CpcSapCode.toUpperCase().trim() === sapCode;
        const prodMatch =
          c.EpdProduct.toUpperCase().trim() === prod ||
          prod.includes(c.EpdProduct.toUpperCase().trim()) ||
          c.EpdProduct.toUpperCase().trim().includes(prod);
        const pkgMatch =
          !c.EpdPackaging ||
          !pkg ||
          c.EpdPackaging.toUpperCase().trim() === pkg ||
          pkg.includes(c.EpdPackaging.toUpperCase().trim()) ||
          c.EpdPackaging.toUpperCase().trim().includes(pkg);

        return divMatch && prodMatch && pkgMatch && sapMatch;
      });

      this.matchedCapacityConfig = match || null;

      if (match) {
        this.lineData.CpcID = match.CpcID;
        if (!this.isExceptionMode && (overrideExisting || !this.lineData.EpdCapacity || this.lineData.EpdCapacity <= 0)) {
          this.lineData.EpdCapacity = Number(match.CpcCapacityTonsFcl);
          this.lineData.EpdIsCapacityException = 0;
          this.lineData.EpdCapacityExceptionReason = "";
        }
      } else {
        if (!this.isExceptionMode) {
          this.lineData.CpcID = null;
          if (overrideExisting) {
            this.lineData.EpdCapacity = 0;
          }
        }
      }
    },
    onDestinationClientSelected(client) {
      if (client) {
        this.lineData.PrsCustomerID = client.CardCode || client.CustomerID || client.value;
        this.lineData.EpdCustomerName = client.CardName || client.CustomerName || client.text;
      } else {
        this.lineData.PrsCustomerID = null;
        this.lineData.EpdCustomerName = "Sin Cliente";
      }
    },
    recalculate() {
      recalculateLine(this.lineData);
    },
    onIncotermChanged() {
      if (this.isFobIncoterm) {
        this.lineData.EpdPriceFobEquivalent = this.lineData.EpdPriceAgreed;
      }
      this.recalculate();
    },
    onPriceAgreedChanged() {
      if (this.isFobIncoterm) {
        this.lineData.EpdPriceFobEquivalent = this.lineData.EpdPriceAgreed;
      }
      this.recalculate();
    },
    submitLine(keepAddingAnother = false) {
      // Limpiar campos de texto si quedaron como objetos
      if (this.lineData.EpdPackaging && typeof this.lineData.EpdPackaging === "object") {
        this.lineData.EpdPackaging = this.lineData.EpdPackaging.DedDescription || this.lineData.EpdPackaging.text || "";
      }
      if (this.lineData.EpdBrand && typeof this.lineData.EpdBrand === "object") {
        this.lineData.EpdBrand = this.lineData.EpdBrand.DedDescription || this.lineData.EpdBrand.text || "";
      }
      if (this.lineData.EpdPaymentCondition && typeof this.lineData.EpdPaymentCondition === "object") {
        this.lineData.EpdPaymentCondition = this.lineData.EpdPaymentCondition.PytDescription || this.lineData.EpdPaymentCondition.text || "";
      }

      // 1. Validar que la capacidad sea mayor que cero
      if (!this.lineData.EpdCapacity || this.lineData.EpdCapacity <= 0) {
        this.$fun.alert("Capacidad no configurada: Debe existir una capacidad en el maestro o registrar una excepción autorizada con capacidad mayor a cero.", "warning");
        return;
      }

      // 2. Si no hay configuración en el maestro y no se activó la excepción, bloquear
      if (!this.matchedCapacityConfig && !this.isExceptionMode) {
        this.$fun.alert("La capacidad no está configurada en el maestro para este producto y empaque. Active la opción 'Usar capacidad diferente / Excepción' para justificarla o regístrela en el maestro de capacidades.", "warning");
        return;
      }

      // 3. Si es excepción, exigir obligatoriamente el motivo de la excepción
      if (this.isExceptionMode) {
        if (!this.lineData.EpdCapacityExceptionReason || !this.lineData.EpdCapacityExceptionReason.trim()) {
          this.$fun.alert("Debe ingresar el motivo o justificación de la excepción de capacidad para registrar esta línea.", "warning");
          return;
        }
        this.lineData.EpdIsCapacityException = 1;
      } else {
        this.lineData.EpdIsCapacityException = 0;
        this.lineData.EpdCapacityExceptionReason = "";
      }

      // 4. Validar cálculos y obligatoriedad de campos
      const validation = validateLine(this.lineData);
      if (!validation.valid) {
        this.$fun.alert(validation.message, "warning");
        return;
      }

      this.recalculate();
      this.$emit("save-line", {
        data: JSON.parse(JSON.stringify(this.lineData)),
        keepAddingAnother,
        isEdit: this.isEdit,
      });
    },
    closeDrawer() {
      this.drawerVisible = false;
    },
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}
.border-top {
  border-top: 1px solid #e2e8f0 !important;
}
.border-left {
  border-left: 1px solid #e2e8f0 !important;
}
.border-light {
  border: 1px solid #e2e8f0 !important;
}
.gap-2 {
  gap: 8px;
}
</style>
